/**
 * Launch gift + welcome gift (Claude-Code-Brief-Launch-and-Welcome-Gift.md).
 * Wording: Litelier-Email-Copy-v1.md, OFFERS section A.
 *
 * The cart only shows the offer and remembers the buyer's choice. The gift
 * is never a cart or Paddle line: its product id (and the welcome link, if
 * any) go to Paddle as custom data, and the Worker decides after payment
 * whether the order really earns it. So nothing here is trusted.
 *
 * - Launch week: the Worker's /gift/config says whether the window is open.
 * - Welcome gift: a personal link `/?gift=<token>` from the welcome email.
 *   The token is kept in this browser and checked with the Worker each visit.
 *
 * Uses the same re-render approach as paddle-checkout.js: the cart drawer
 * morphs its markup on every change, so the gift box is redrawn from a
 * MutationObserver and only touched when its content actually changes.
 */
const WORKER_URL = 'https://litelier-patterns-worker.litelier.workers.dev';
const CHOICE_KEY = 'litelier-gift-choice';
const WELCOME_KEY = 'litelier-welcome-gift';
const HEART = '<span class="lk-star" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true" focusable="false"><path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.7L12 16.6l-5.1 2.7 1-5.7-4.1-4 5.7-.8z" fill="#C8922C" stroke="#C8922C" stroke-width="2.2" stroke-linejoin="round"/></svg></span>';

// ---------- storage (may be unavailable: private windows, blocked storage) ----------

function readStored(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

function writeStored(key, value) {
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The picker still works for this page view.
  }
}

/**
 * The chosen gift is kept in localStorage AND a first-party cookie (product
 * id + title only), so it survives reloads and drawer/cart-page switches even
 * where localStorage is blocked (private windows), and is the same in the
 * drawer and on the cart page.
 */
const CHOICE_COOKIE = 'litelier_gift';

function readChoiceCookie() {
  const match = document.cookie.match(/(?:^|; )litelier_gift=([^;]*)/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return parsed && parsed.productId ? parsed : null;
  } catch {
    return null;
  }
}

function writeChoiceCookie(choice) {
  const value = choice ? encodeURIComponent(JSON.stringify({ productId: String(choice.productId), title: choice.title })) : '';
  document.cookie = `${CHOICE_COOKIE}=${value}; path=/; max-age=${choice ? 7 * 24 * 3600 : 0}; SameSite=Lax`;
}

let memoryChoice = readStored(CHOICE_KEY) || readChoiceCookie();

function getChoice() {
  return memoryChoice;
}

function setChoice(choice) {
  memoryChoice = choice;
  writeStored(CHOICE_KEY, choice);
  writeChoiceCookie(choice);
}

/** After a successful payment that order used the choice, so the next order starts fresh. */
function clearChoiceAfterPurchase() {
  if (new URL(window.location.href).searchParams.get('paid') === '1') setChoice(null);
  document.addEventListener('litelier:paddle', (event) => {
    if (event.detail?.name === 'checkout.completed') setChoice(null);
  });
}

/** What the gift box last showed, sent with the order so a missing gift can be explained later. */
let lastUiState = 'not-rendered';

/** `?gift=<token>` from the welcome email: keep it, then tidy the address bar. */
function captureWelcomeLink() {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('gift');
  if (!token) return;
  writeStored(WELCOME_KEY, token);
  url.searchParams.delete('gift');
  window.history.replaceState({}, '', url.toString());
}

// ---------- offer state ----------

let configPromise = null;
let welcomePromise = null;

/**
 * Gift settings are kept in this browser for 5 minutes so the gift box shows
 * at once instead of appearing a few seconds later. A saved "launch week is
 * on" stops counting at its own end time, so the box never outlives the offer.
 */
const CONFIG_CACHE_KEY = 'litelier-gift-config';
const CONFIG_CACHE_MS = 5 * 60 * 1000;

function readCachedConfig() {
  const cached = readStored(CONFIG_CACHE_KEY);
  if (!cached || Date.now() - cached.at > CONFIG_CACHE_MS || !cached.data) return null;
  const data = cached.data;
  if (data.launch?.active && data.launch.endsAt && Date.parse(data.launch.endsAt) <= Date.now()) {
    return { ...data, launch: { active: false, endsAt: null } };
  }
  return data;
}

function getConfig() {
  const cached = !configPromise && readCachedConfig();
  if (cached) configPromise = Promise.resolve(cached);
  if (!configPromise) {
    configPromise = fetch(`${WORKER_URL}/gift/config`)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null)
      .then((config) => {
        if (!config) configPromise = null; // try again on the next render
        else writeStored(CONFIG_CACHE_KEY, { at: Date.now(), data: config });
        return config;
      });
  }
  return configPromise;
}

/** The stored welcome token when the Worker says it still unlocks the gift, otherwise null. */
function getWelcomeToken() {
  const token = readStored(WELCOME_KEY);
  if (!token) return Promise.resolve(null);
  if (!welcomePromise) {
    welcomePromise = fetch(`${WORKER_URL}/gift/check?t=${encodeURIComponent(token)}`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((result) => {
        if (result && result.valid) return token;
        if (result && !result.valid) writeStored(WELCOME_KEY, null); // expired or already used
        return null;
      })
      .catch(() => null);
  }
  return welcomePromise;
}

/**
 * Signed-in buyers only see patterns they don't own yet. Someone who already
 * owns every single (e.g. a Complete Library buyer) has nothing to choose, so
 * the gift box stays hidden instead of opening an empty picker.
 */
let signedInOptionsPromise = null;

function getSignedInOptions() {
  if (!window.__litelierCustomerEmail) return Promise.resolve(null);
  if (!signedInOptionsPromise) {
    signedInOptionsPromise = fetch('/apps/library/gift-options', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => (data && Array.isArray(data.options) ? data.options : null))
      .catch(() => null)
      .then((options) => {
        if (!options) signedInOptionsPromise = null; // unknown: fall back to the full list, try again later
        return options;
      });
  }
  return signedInOptionsPromise;
}

async function getOffer() {
  const [config, welcomeToken] = await Promise.all([getConfig(), getWelcomeToken()]);
  if (!config) return null;
  const active = Boolean(config.launch?.active || welcomeToken);
  if (!active) return null;
  const signedInOptions = await getSignedInOptions();
  if (signedInOptions && signedInOptions.length === 0) return null;
  // Welcome gift only (outside launch week): the gift needs the signup email at checkout (Gina Oct 18).
  return { minCents: config.minCents, welcomeToken, welcomeOnly: Boolean(welcomeToken && !config.launch?.active) };
}

function formatUsd(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

// ---------- cart box ----------

async function cartState() {
  const cart = window.__litelierCart;
  if (!cart) return null;
  const rows = cart.readCartRows();
  if (rows.length === 0) return null;
  const offer = await getOffer();
  if (!offer) return null;

  const inCart = new Set(rows.map((r) => r.productId));
  let choice = getChoice();
  // Nothing already in the cart can be the gift.
  if (choice && inCart.has(String(choice.productId))) {
    setChoice(null);
    choice = null;
  }
  const totalCents = await cart.estimateCartCents(rows);
  return { offer, rows, inCart, choice, totalCents, unlocked: totalCents >= offer.minCents };
}

const EMAIL_LINE = '<p class="cart-gift__email" style="margin:6px 0 0;font-size:0.85em;opacity:0.85">Use the email you signed up with at checkout, so your gift finds you.</p>';

// Oct 21 (audit M-09): launch gift and welcome gift never stack.
const ONE_LINE = '<p class="cart-gift__one" style="margin:6px 0 0;font-size:0.85em;opacity:0.85">One free pattern per order.</p>';

function boxHtml(state) {
  return boxMainHtml(state) + ONE_LINE + (state.offer.welcomeOnly ? EMAIL_LINE : '');
}

function boxMainHtml(state) {
  if (!state.unlocked) {
    const missing = state.offer.minCents - state.totalCents;
    return `<p class="cart-gift__progress">You’re ${formatUsd(missing)} away from a free pattern ${HEART}</p>`;
  }
  if (state.choice) {
    return `<p class="cart-gift__line">Your free gift: ${escapeHtml(state.choice.title)} · <strong>$0</strong> ${HEART} · <button type="button" class="cart-gift__change" data-gift-open><em>Change</em></button></p>`;
  }
  // T-457: a clear button as soon as the order qualifies.
  return `<p class="cart-gift__line">You've unlocked a free pattern ${HEART}</p><button type="button" class="button cart-gift__choose" data-gift-open>Choose your free pattern</button>`;
}

function setHtml(element, html) {
  if (element.dataset.giftRendered === html && element.firstChild) return;
  element.dataset.giftRendered = html;
  element.innerHTML = html;
}

let rendering = false;
let renderAgain = false;

async function renderCartGift() {
  if (rendering) {
    renderAgain = true;
    return;
  }
  rendering = true;
  try {
    if (!document.querySelector('[data-gift-box]')) return;
    const state = await cartState();
    // Looked up after the wait: the drawer may have swapped these nodes meanwhile.
    const boxes = [...document.querySelectorAll('[data-gift-box]')];
    const notes = [...document.querySelectorAll('[data-gift-note]')];
    lastUiState = !state ? 'hidden' : !state.unlocked ? 'below-minimum' : state.choice ? 'chosen' : 'unlocked';
    for (const box of boxes) {
      if (!state) {
        if (!box.hidden) box.hidden = true;
        continue;
      }
      setHtml(box, boxHtml(state));
      if (box.hidden) box.hidden = false;
    }
    for (const note of notes) {
      const show = Boolean(state && state.unlocked);
      if (note.hidden === show) note.hidden = !show;
    }
  } finally {
    rendering = false;
    if (renderAgain) {
      renderAgain = false;
      renderCartGift();
    }
  }
}

// ---------- picker ----------

let dialog = null;

function ensureDialog() {
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'gift-picker';
  dialog.setAttribute('data-gift-picker', '');
  dialog.innerHTML = `
    <div class="gift-picker__header">
      <h2 class="gift-picker__title h4">Choose your free pattern ${HEART}</h2>
      <button type="button" class="gift-picker__close" data-gift-close aria-label="Close">×</button>
    </div>
    <div class="gift-picker__grid" data-gift-grid></div>
    <div class="gift-picker__footer">
      <button type="button" class="gift-picker__done" data-gift-done disabled>Tap a pattern to choose it</button>
    </div>`;
  document.body.appendChild(dialog);
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('[data-gift-close]')) dialog.close();
  });
  return dialog;
}

async function loadOptions(mode) {
  // Signed-in buyers get the list without patterns they already own (through the App Proxy).
  if (mode === 'cart') {
    const signedIn = await getSignedInOptions();
    if (signedIn && signedIn.length > 0) return signedIn;
  }
  const sources = mode === 'library' ? ['/apps/library/gift-options'] : [`${WORKER_URL}/gift/options`];
  for (const source of sources) {
    try {
      const response = await fetch(source, { cache: 'no-store' });
      if (!response.ok) continue;
      const data = await response.json();
      if (Array.isArray(data.options)) return data.options;
    } catch {
      // try the next source
    }
  }
  return null;
}

function sizedImageUrl(url) {
  try {
    const sized = new URL(url);
    sized.searchParams.set('width', '300');
    return sized.toString();
  } catch {
    return url;
  }
}

function optionHtml(option, chosenId) {
  const image = option.imageUrl
    ? `<img src="${escapeHtml(sizedImageUrl(option.imageUrl))}" alt="" loading="lazy" width="150" height="150">`
    : '<span class="gift-picker__image-placeholder"></span>';
  const isChosen = String(option.productId) === String(chosenId);
  const chosen = isChosen ? ' gift-picker__option--chosen' : '';
  return `<button type="button" class="gift-picker__option${chosen}" aria-pressed="${isChosen}" data-gift-choose="${escapeHtml(option.productId)}" data-gift-title="${escapeHtml(option.title)}">${image}<span class="gift-picker__check" aria-hidden="true">✓</span><span class="gift-picker__name">${escapeHtml(option.title)}</span></button>`;
}

async function openPicker(mode, order = '') {
  const picker = ensureDialog();
  picker.dataset.mode = mode;
  picker.dataset.order = order;
  const grid = picker.querySelector('[data-gift-grid]');
  grid.innerHTML = '<p class="gift-picker__status">Loading patterns…</p>';
  picker.querySelector('[data-gift-picker-note]')?.remove();
  updateDoneButton();
  if (!picker.open) picker.showModal();

  const options = await loadOptions(mode);
  if (!options) {
    grid.innerHTML = `<p class="gift-picker__status">Couldn't load the patterns. Please refresh the page ${HEART}</p>`;
    return;
  }
  const inCart = mode === 'cart' && window.__litelierCart ? new Set(window.__litelierCart.readCartRows().map((r) => r.productId)) : new Set();
  const chosenId = mode === 'cart' ? getChoice()?.productId : null;
  const shown = options.filter((o) => !inCart.has(String(o.productId)));
  if (shown.length === 0) {
    grid.innerHTML = `<p class="gift-picker__status">Couldn't load the patterns. Please refresh the page ${HEART}</p>`;
    return;
  }
  grid.innerHTML = shown.map((o) => optionHtml(o, chosenId)).join('');
  updateDoneButton();
}

async function choose(button) {
  const productId = button.getAttribute('data-gift-choose');
  const title = button.getAttribute('data-gift-title');
  if (dialog.dataset.mode === 'library') {
    button.disabled = true;
    const done = dialog.querySelector('[data-gift-done]');
    if (done) {
      done.disabled = true;
      done.textContent = 'Adding it to your library…';
    }
    const response = await fetch('/apps/library/gift-choose', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The library banner is per order: the choice goes to that order only.
      body: JSON.stringify({ productId, ...(dialog.dataset.order ? { order: dialog.dataset.order } : {}) }),
    }).catch(() => null);
    if (response && response.ok) {
      window.location.reload();
      return;
    }
    // T-499: say why instead of doing nothing (already picked elsewhere, time ended, already owned).
    const result = response ? await response.json().catch(() => ({})) : {};
    const messages = {
      no_gift_waiting: 'This free pattern was already picked. Refresh the page to see it in your library.',
      gift_expired: 'The time to pick this free pattern has ended.',
      already_owned: 'You already own this pattern. Please pick another one.',
    };
    const pickerGrid = dialog.querySelector('[data-gift-grid]');
    const note = dialog.querySelector('[data-gift-picker-note]') || pickerGrid.insertAdjacentElement('beforebegin', Object.assign(document.createElement('p'), { className: 'gift-picker__status' }));
    note.setAttribute('data-gift-picker-note', '');
    note.textContent = messages[result.error] || "Couldn't save your choice. Please try again.";
    button.disabled = false;
    updateDoneButton();
    return;
  }
  setChoice({ productId, title });
  dialog.close();
  renderCartGift();
}

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-gift-open]');
  if (open) {
    event.preventDefault();
    // T-457 / report 5.9: never a picker under the $9.95 minimum.
    if (lastUiState === 'below-minimum' || lastUiState === 'hidden') return;
    openPicker('cart');
    return;
  }
  const libraryOpen = event.target.closest('[data-gift-library-open]');
  if (libraryOpen) {
    event.preventDefault();
    openPicker('library', libraryOpen.getAttribute('data-gift-order') || '');
    return;
  }
  // Gina, Oct 23: a tap only SELECTS (a tap while scrolling must never pick); the floating
  // button at the bottom confirms the choice.
  const option = event.target.closest('[data-gift-choose]');
  if (option && dialog && dialog.contains(option)) {
    event.preventDefault();
    selectOption(option);
    return;
  }
  const done = event.target.closest('[data-gift-done]');
  if (done && dialog && dialog.contains(done) && !done.disabled) {
    event.preventDefault();
    const selected = dialog.querySelector('.gift-picker__option--chosen');
    if (selected) choose(selected);
  }
});

function selectOption(option) {
  for (const other of dialog.querySelectorAll('.gift-picker__option--chosen')) {
    other.classList.remove('gift-picker__option--chosen');
    other.setAttribute('aria-pressed', 'false');
  }
  option.classList.add('gift-picker__option--chosen');
  option.setAttribute('aria-pressed', 'true');
  updateDoneButton();
}

function updateDoneButton() {
  const done = dialog && dialog.querySelector('[data-gift-done]');
  if (!done) return;
  const selected = dialog.querySelector('.gift-picker__option--chosen');
  done.disabled = !selected;
  if (!selected) {
    done.textContent = 'Tap a pattern to choose it';
    return;
  }
  const title = selected.getAttribute('data-gift-title');
  done.textContent = dialog.dataset.mode === 'library' ? `Add “${title}” to my library` : `Choose “${title}”`;
}

// ---------- checkout ----------

/**
 * Custom data for Paddle: the chosen pattern when the cart reaches the
 * minimum, and the welcome link whenever it is valid (so an order that
 * qualifies without a choice still gets the library banner). Never throws:
 * a gift problem must not stop a checkout.
 */
async function checkoutData(rows) {
  // No checks here on purpose: the Worker decides (offer dates, $9.95, first
  // order, owned…). Earlier the browser re-checked the offer and the total and
  // could silently drop a real choice when one of those requests failed.
  try {
    const data = {};
    const welcome = readStored(WELCOME_KEY);
    if (welcome) data.welcome = String(welcome);
    const choice = getChoice();
    const inCart = new Set(rows.map((r) => r.productId));
    // T-457 / report 5.9: a choice left over from a bigger cart is not sent with an order under the minimum.
    if (choice && !inCart.has(String(choice.productId)) && lastUiState !== 'below-minimum') data.pattern = String(choice.productId);
    if (lastUiState !== 'not-rendered' && lastUiState !== 'hidden') data.ui = lastUiState;
    return Object.keys(data).length > 0 ? data : null;
  } catch (error) {
    console.error('Gift data skipped:', error);
    return null;
  }
}

window.__litelierGift = { checkoutData, openPicker };

// ---------- start ----------

function watchCart() {
  let scheduled = false;
  // Morphs also reset the gift box itself, so changes inside it count too (writes are skipped when unchanged).
  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((m) => {
      const element = m.target instanceof Element ? m.target : m.target.parentElement;
      return !(element && element.closest('[data-gift-picker], [data-cart-duplicate-note]'));
    });
    if (!relevant || scheduled) return;
    scheduled = true;
    // A timer, not requestAnimationFrame: frames pause in background tabs.
    window.setTimeout(() => {
      scheduled = false;
      renderCartGift();
    }, 30);
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
}

captureWelcomeLink();
clearChoiceAfterPurchase();
// paddle-checkout.js may load after this file; it announces when its cart helpers exist.
document.addEventListener('litelier:cart-ready', () => renderCartGift());
renderCartGift();
watchCart();
